import styled from '@emotion/styled';
import { useIsMobile, useIsTablet } from '@hooks/responsive/useIsMobile';
import Image from 'next/image';
import Link from 'next/link';

// components
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';

// constants
import { ButtonColor, TypoVariant } from '@constants/atoms';
import { Keyframes } from '@constants/keyframes';
import { MOBILE_MODE, TABLET_MODE } from '@constants/size';
import { blue, gray } from '@styles/Colors';
import { useTranslation } from 'next-i18next';
import ButtonCode from '@constants/log/buttonCode';

type Props = {
  fileUrl: string;
};

const Layout = styled.div`
  padding: 216px 0 160px;

  @media ${TABLET_MODE} {
    padding: 176px 0 120px;
  }
  @media ${MOBILE_MODE} {
    padding: 110px 0 80px;
  }
`;

const Content = styled.div`
  max-width: 1920px;
  margin: 0 auto;
`;

const CommonWidth = `
  max-width: 1160px;
  margin: 0 auto;
`;
const CommonPadding = `
  padding:0 40px;

  @media ${MOBILE_MODE} {
    padding: 0 20px;
  }
`;
const CommonMarginTop = `
  margin-top: 120px;

  @media ${TABLET_MODE} {
    margin-top: 80px;
  }
  @media ${MOBILE_MODE} {
    margin-top: 40px;
  }
`;

const DescriptionBox = styled.div`
  ${CommonWidth};
  ${CommonPadding};

  display: grid;
  row-gap: 16px;
`;

const ButtonWrapper = styled.div`
  ${CommonWidth};
  ${CommonPadding};
  margin-top: 40px;

  display: flex;
`;

const SliderWrapper = styled.div`
  ${CommonMarginTop};
  overflow: hidden;
  display: flex;
`;

const CardWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  column-gap: 24px;
  padding-right: 24px;

  animation: ${Keyframes.RowSlider} 33s linear infinite;
`;

const CardCloneWrapper = styled(CardWrapper)`
  animation: ${Keyframes.RowSliderClone} 33s linear infinite;
`;

const CardImageWrapper = styled.div`
  width: 480px;
  height: 360px;

  position: relative;

  @media ${TABLET_MODE} {
    width: 400px;
    height: 300px;
  }
  @media ${MOBILE_MODE} {
    width: 240px;
    height: 180px;
  }
`;

const ExplainWrapper = styled.div`
  ${CommonWidth};
  ${CommonPadding};
  ${CommonMarginTop};

  display: grid;
  grid-template-columns: 182px 1fr;
  column-gap: 185px;

  @media ${TABLET_MODE} {
    grid-template-columns: none;
    row-gap: 40px;
  }
`;

const LogoWrapper = styled.div`
  width: 182px;
  height: 84px;
  position: relative;
`;

const TextWrapper = styled.div`
  display: grid;
  row-gap: 40px;
`;

const HighLightBlue = styled.span`
  color: ${blue.blue3};
`;

export default function MainSection({ fileUrl }: Props) {
  const { t, i18n } = useTranslation('intro');
  const ko = i18n.language === 'ko';

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  return (
    <Layout>
      <Content>
        <DescriptionBox>
          <Typography variant={isMobile ? TypoVariant.H5 : isTablet ? TypoVariant.H4 : TypoVariant.H3}>
            {t('main-section.description-text')}
          </Typography>
          <Typography variant={isMobile ? TypoVariant.B3 : isTablet ? TypoVariant.B2 : TypoVariant.B1}>
            {t('main-section.description-sub-text1')}
            <br />
            {t('main-section.description-sub-text2')}
          </Typography>
        </DescriptionBox>
        <ButtonWrapper>
          <a href={fileUrl} download>
            <Button
              text={t('main-section.description-download-text')}
              color={ButtonColor.OutlinedBlue}
              steelbosoId={ButtonCode.BTN_COMPANY_TOP_FILE_DOWNLOAD}
            />
          </a>
        </ButtonWrapper>

        <SliderWrapper>
          <CardWrapper>
            <Cards />
          </CardWrapper>
          <CardCloneWrapper>
            <Cards />
          </CardCloneWrapper>
        </SliderWrapper>

        <ExplainWrapper>
          <LogoWrapper>
            <Image src="/assets/common/logo-lg.png" layout="fill" alt="logo-lg" />
          </LogoWrapper>

          <TextWrapper>
            {ko ? (
              <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B1} color={gray.gray7}>
                마켓오브메테리얼은 2019년 10월 공식 출범 이후 인공지능(AI), 빅데이터, 검색 등 오랜 시간 동안 축적한
                기술력과 서비스 경험을 결합하여 시장 모든 파트너들과 함께 상생할 수 있는 플랫폼{' '}
                <HighLightBlue>steelboso</HighLightBlue>를 서비스하고 있습니다.
              </Typography>
            ) : (
              <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B1} color={gray.gray7}>
                Since our official launch in October 2019, Market of Material has been providing the{' '}
                <HighLightBlue>Steelboso</HighLightBlue> platform, which combines our extensive technological expertise
                and service experience in areas such as artificial intelligence (AI), big data, and search. Through this
                platform, we aim to create a mutually beneficial environment for all market partners to thrive together.
              </Typography>
            )}
            <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B1} color={gray.gray7}>
              {t('main-section.explain-text')}
            </Typography>
          </TextWrapper>
        </ExplainWrapper>
      </Content>
    </Layout>
  );

  function Cards() {
    return (
      <>
        {[1, 2, 3, 4, 5, 6].map((idx) => (
          <CardImageWrapper key={idx}>
            <Image src={`/assets/images/intro-card-${idx}.webp`} layout="fill" alt={`card-${idx}`} />
          </CardImageWrapper>
        ))}
      </>
    );
  }
}
